import { selectActiveFirm } from "@/store/appSelectors";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/DatePicker";
import { showApiError, showSuccess } from "@/lib/toast";
import { getContentDetails, updateContentDates, updateContentFolder } from "@/sideEffects/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { LoadingSmall } from "@/components/LoadingSmall";
import { parse } from "date-fns";
import { t } from "i18next";
import { formatDateToLocal, formatToUTC } from "@/lib/dateUtils";
import { ContentFolderBaseView } from "./components/folder/ContentFolderBaseView";
interface Props {
  contentGuid: string;
  onSuccess: () => void;
  onOpenChange: (open: boolean) => void;
}

export const UpdateContentFolderDialog: React.FC<Props> = ({ contentGuid, onSuccess, onOpenChange }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const content = useQuery({
    queryKey: ["content", contentGuid],
    queryFn: () => getContentDetails(String(activeFirm?.firmGuid), contentGuid),
  });

  const updateContentFolderMutation = useMutation({
    mutationFn: (data: { contentGuid: string; folderGuid: string }) =>
      updateContentFolder(String(activeFirm?.firmGuid), data.contentGuid, data.folderGuid),
    onSuccess: () => {
      onSuccess();
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const onSelectFolder = (folderGuid?: string) => {
    updateContentFolderMutation.mutate({
      contentGuid,
      folderGuid: folderGuid || "",
    });
  };

  if (content.isLoading) {
    return (
      <Sheet open onOpenChange={onOpenChange}>
        <SheetContent className="overflow-y-auto">
          <LoadingSmall />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("entities.folder")}</SheetTitle>
          <ContentFolderBaseView onSelectFolder={onSelectFolder} selectedFolderGuid={content.data?.data.folderGuid} />
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};
